import { FocusEvent, useEffect, useRef, useState } from 'react';
import type { MoneyInput } from 'v5/core/money/moneyInput';

/**
 * A number field edited as text: what the person typed stays on screen while
 * they type, the form gets `number | null` on every keystroke, and readable
 * text is tidied up on blur ("1250,5" → "1,250.50") unless the tidy text
 * would read as another number ("850.123" stays, validation explains it).
 *
 * Unreadable text is reported as `NaN` rather than `null`, so validation can
 * say "enter a number" instead of treating it as empty and letting it save
 * as nothing.
 */
export type DraftNumberOptions = {
  value: number | null;
  onChange(value: number | null): void;
  onBlur?(event: FocusEvent<HTMLInputElement>): void;
  /** Committed value → text shown in the input. */
  format(value: number): string;
  /** Text → value; `null` when empty, `NaN` when unreadable. */
  parse(text: string): number | null;
};

export type DraftNumberInput = {
  value: string;
  onChange(event: { target: { value: string } }): void;
  onBlur(event: FocusEvent<HTMLInputElement>): void;
};

/** A parsed `MoneyInput` as a draft value: empty → `null`, unreadable → `NaN`. */
export function draftNumberOf(input: MoneyInput): number | null {
  switch (input.type) {
    case 'empty':
      return null;
    case 'invalid':
      return Number.NaN;
    case 'number':
      return input.value;
  }
}

function toText(
  value: number | null,
  format: (value: number) => string
): string {
  return value === null || Number.isNaN(value) ? '' : format(value);
}

export function useDraftNumber({
  value,
  onChange,
  onBlur,
  format,
  parse
}: DraftNumberOptions): DraftNumberInput {
  const [text, setText] = useState(() => toText(value, format));
  /** Last value this input reported; anything else came from outside (reset, load). */
  const reported = useRef<number | null>(value);

  useEffect(() => {
    if (Object.is(value, reported.current)) return;
    reported.current = value;
    setText(toText(value, format));
    // Only an outside change of `value` re-syncs; `format` identity is irrelevant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return {
    value: text,
    onChange(event) {
      const next = parse(event.target.value);
      setText(event.target.value);
      reported.current = next;
      onChange(next);
    },
    onBlur(event) {
      const parsed = parse(text);
      if (parsed !== null && Number.isFinite(parsed)) {
        const tidy = format(parsed);
        if (parse(tidy) === parsed) setText(tidy);
      }
      onBlur?.(event);
    }
  };
}
